import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const socials = [
  { label: "Instagram", href: "https://www.instagram.com/graindedesign/?hl=fr" },
  { label: "Behance", href: "https://www.behance.net/chlorigalla" },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/chloé-rigallaud-leal-b69b9a225/" },
];

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t("contact.error"));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#2A2A2A] focus:border-[#75FB4E] outline-none py-4 text-base text-[#E5E5E5] placeholder:text-[#4a4a4a] transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="px-6 md:px-12 py-28 md:py-40 border-t border-[#1A1A1A]">
      <FadeUp className="flex items-baseline gap-4 mb-16 md:mb-24">
        <span className="font-mono-tech text-sm text-[#75FB4E]">(04)</span>
        <h2 className="font-display text-5xl md:text-8xl uppercase leading-none">{t("contact.section")}</h2>
      </FadeUp>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
        <FadeUp delay={0.1}>
          <p className="text-2xl md:text-4xl font-light leading-snug mb-12">
            {t("contact.hookTop")}<br />
            <span className="text-[#808080]">{t("contact.hookBottom")}</span>
          </p>
          <a
            data-testid="contact-email-link"
            href="mailto:c.rigallaudleal@gmail.com"
            className="group inline-flex items-center gap-3 font-mono-tech text-sm md:text-base text-[#E5E5E5] hover:text-[#75FB4E] transition-colors duration-300 mb-4"
          >
            c.rigallaudleal@gmail.com
            <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-300" />
          </a>
          <a
            data-testid="contact-phone-link"
            href="tel:+33631978907"
            className="group flex items-center gap-3 font-mono-tech text-sm md:text-base text-[#E5E5E5] hover:text-[#75FB4E] transition-colors duration-300 mb-12"
          >
            06 31 97 89 07
            <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-300" />
          </a>
          <div className="flex flex-col gap-3 border-t border-[#1A1A1A] pt-8">
            {socials.map((s) => (
              <a
                key={s.label}
                data-testid={`social-link-${s.label.toLowerCase()}`}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between font-mono-tech text-xs tracking-[0.25em] uppercase text-[#808080] hover:text-[#E5E5E5] transition-colors duration-300"
              >
                {s.label}
                <ArrowUpRight className="w-4 h-4 group-hover:text-[#75FB4E] group-hover:rotate-45 transition-all duration-300" />
              </a>
            ))}
          </div>
        </FadeUp>

        <FadeUp delay={0.2}>
          <form data-testid="contact-form" onSubmit={handleSubmit} className="flex flex-col gap-8">
            <div>
              <label className="font-mono-tech text-[10px] tracking-[0.3em] text-[#808080] uppercase">{t("contact.name")}</label>
              <input
                data-testid="contact-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("contact.namePlaceholder")}
                className={inputCls}
              />
            </div>
            <div>
              <label className="font-mono-tech text-[10px] tracking-[0.3em] text-[#808080] uppercase">{t("contact.email")}</label>
              <input
                data-testid="contact-email-input"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t("contact.emailPlaceholder")}
                className={inputCls}
              />
            </div>
            <div>
              <label className="font-mono-tech text-[10px] tracking-[0.3em] text-[#808080] uppercase">{t("contact.message")}</label>
              <textarea
                data-testid="contact-message-input"
                required
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t("contact.messagePlaceholder")}
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="group relative self-start overflow-hidden border border-[#2A2A2A] px-10 py-4 font-mono-tech text-xs tracking-[0.3em] uppercase text-[#E5E5E5] hover:border-[#75FB4E] transition-colors duration-300 disabled:opacity-50"
            >
              <span className="absolute inset-0 bg-[#75FB4E] translate-y-full group-hover:translate-y-0 transition-transform duration-400 ease-out" />
              <span className="relative group-hover:text-[#050505] transition-colors duration-300">
                {sending ? t("contact.sending") : t("contact.send")}
              </span>
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
}
